import type { Messenger } from "../messaging/messenger"
import {
  barcodeScanned,
  barcodeScannerConnected,
  barcodeScannerDisconnected,
  barcodeScannerUnsupported,
  type BarcodeScanner,
  type BarcodeScannerDevice,
} from "./barcode-scanner"

// Adapted from Niels Leenheer's WebHidBarcodeScanner (MIT).
// See packages/framework/LICENSES/webhid-barcode-scanner.txt.

const scannerFilters = [
  { vendorId: 0x0c2e }, // Honeywell
  { vendorId: 0x23d0 }, // Youjie
  { vendorId: 0x05e0 }, // Symbol / Zebra
  { vendorId: 0x05f9 }, // Datalogic
] as const

interface HidCollectionInfo {
  readonly usage: number
  readonly usagePage: number
}

interface HidDeviceLike extends EventTarget {
  readonly vendorId: number
  readonly productId: number
  readonly productName?: string
  readonly collections: readonly HidCollectionInfo[]
  readonly opened: boolean
  open(): Promise<void>
  close(): Promise<void>
  sendFeatureReport(reportId: number, data: BufferSource): Promise<void>
}

interface HidInputReportEventLike extends Event {
  readonly data: DataView
  readonly device: HidDeviceLike
  readonly reportId: number
}

interface HidApiLike extends EventTarget {
  requestDevice(options: { filters: readonly { vendorId: number }[] }): Promise<HidDeviceLike[]>
  getDevices(): Promise<HidDeviceLike[]>
}

type NavigatorWithHid = Navigator & { hid?: HidApiLike }

export interface WebHidBarcodeScannerOptions {
  readonly debug?: boolean
}

export function createWebHidBarcodeScanner(
  messenger: Messenger,
  options: WebHidBarcodeScannerOptions = {}
): BarcodeScanner {
  const hid = (globalThis.navigator as NavigatorWithHid | undefined)?.hid
  const debug = options.debug ?? false
  let device: HidDeviceLike | null = null
  let buffer = ""
  let listening = false
  let disposed = false

  const toDeviceInfo = (value: HidDeviceLike): BarcodeScannerDevice => ({
    transport: "webhid",
    vendorId: value.vendorId,
    productId: value.productId,
    productName: value.productName,
  })

  const isBarcodeScanner = (value: HidDeviceLike) =>
    value.collections.some((collection) => collection.usage === 2 && collection.usagePage === 140)

  const logDevice = (message: string, value: HidDeviceLike) => {
    if (!debug) return
    const collectionSnapshot = value.collections.map((collection) => ({
      usagePage: collection.usagePage,
      usagePageHex: formatHex(collection.usagePage),
      usage: collection.usage,
      usageHex: formatHex(collection.usage),
      raw: collection,
    }))
    console.log(`[BarcodeScanner/WebHID] ${message}`, {
      productName: value.productName,
      vendorId: formatHex(value.vendorId),
      productId: formatHex(value.productId),
      opened: value.opened,
      isHidPos: isBarcodeScanner(value),
      collections: collectionSnapshot,
      raw: value,
    })
    console.log(`[BarcodeScanner/WebHID] ${message} descriptor\n${JSON.stringify(collectionSnapshot, null, 2)}`)
  }

  const handleInputReport = (event: Event) => {
    const input = event as HidInputReportEventLike
    if (debug) {
      const bytes = Array.from(new Uint8Array(input.data.buffer, input.data.byteOffset, input.data.byteLength))
      console.log("[BarcodeScanner/WebHID] input report", {
        reportId: input.reportId,
        reportIdHex: formatHex(input.reportId),
        byteLength: input.data.byteLength,
        bytes,
        hex: bytes.map((byte) => byte.toString(16).padStart(2, "0")).join(" "),
      })
    }
    if (input.reportId !== 0x02) {
      if (debug) console.log("[BarcodeScanner/WebHID] report ignored: expected HID POS report id 0x02")
      return
    }

    const { data } = input
    if (data.byteLength < 63) {
      if (debug) console.log("[BarcodeScanner/WebHID] report ignored: HID POS report is shorter than 63 bytes")
      return
    }
    let offset = -1
    if (data.getUint8(1) === 0x5d) offset = 0
    if (data.getUint8(2) === 0x5d) offset = 1
    if (offset < 0) {
      if (debug) console.log("[BarcodeScanner/WebHID] report ignored: AIM marker 0x5d not found")
      return
    }

    const valueLength = data.getUint8(0)
    const valueOffset = offset + 4
    if (valueOffset + valueLength > data.byteLength) {
      if (debug) console.log("[BarcodeScanner/WebHID] report ignored: barcode payload exceeds report length")
      buffer = ""
      return
    }

    for (let index = 0; index < valueLength; index += 1) {
      buffer += String.fromCharCode(data.getUint8(index + offset + 4))
    }
    if (data.getUint8(62)) return

    let value = buffer
    if (value.endsWith("\r")) value = value.slice(0, -1)
    const aim = String.fromCharCode(data.getUint8(1 + offset), data.getUint8(2 + offset), data.getUint8(3 + offset))

    messenger.emit(
      barcodeScanned,
      Object.freeze({
        value,
        aim: aim || undefined,
        symbology: isHoneywell(input.device) ? honeywellSymbologies[data.getUint8(60)] : undefined,
        device: toDeviceInfo(input.device),
      })
    )

    buffer = ""
  }

  const handleConnect = () => {
    if (device == null && !disposed) {
      void reconnect().catch((error) => {
        if (debug) console.error("[BarcodeScanner/WebHID] reconnect after device connection failed", error)
      })
    }
  }

  const handleDisconnect = (event: Event) => {
    const disconnected = (event as Event & { device?: HidDeviceLike }).device
    if (disconnected == null || disconnected !== device) return
    disconnected.removeEventListener("inputreport", handleInputReport)
    device = null
    buffer = ""
    messenger.emit(barcodeScannerDisconnected, toDeviceInfo(disconnected))
  }

  const startListening = () => {
    if (hid == null || listening) return
    listening = true
    hid.addEventListener("connect", handleConnect)
    hid.addEventListener("disconnect", handleDisconnect)
  }

  const stopListening = () => {
    if (hid == null || !listening) return
    listening = false
    hid.removeEventListener("connect", handleConnect)
    hid.removeEventListener("disconnect", handleDisconnect)
  }

  async function closeCurrent() {
    if (device == null) return
    const current = device
    current.removeEventListener("inputreport", handleInputReport)
    device = null
    buffer = ""
    try {
      if (current.opened) await current.close()
    } finally {
      messenger.emit(barcodeScannerDisconnected, toDeviceInfo(current))
    }
  }

  async function open(value: HidDeviceLike) {
    if (device === value) return true
    if (device != null) await closeCurrent()
    logDevice("opening device", value)
    const openedHere = !value.opened
    if (openedHere) await value.open()
    try {
      logDevice("opened device", value)
      if (isHoneywell(value)) await value.sendFeatureReport(254, new Uint8Array([0x01]))
    } catch (error) {
      if (openedHere && value.opened) await value.close()
      throw error
    }

    device = value
    buffer = ""
    value.addEventListener("inputreport", handleInputReport)
    messenger.emit(barcodeScannerConnected, toDeviceInfo(value))
    return true
  }

  async function connect() {
    if (hid == null || disposed) return false
    startListening()
    const selected = await hid.requestDevice({ filters: scannerFilters })
    if (debug) {
      console.groupCollapsed(`[BarcodeScanner/WebHID] selected ${selected.length} device(s)`)
      for (const selectedDevice of selected) logDevice("selected device", selectedDevice)
      console.groupEnd()
    }
    const scanner = selected.find(isBarcodeScanner)
    if (scanner == null) {
      messenger.emit(barcodeScannerUnsupported, {
        transport: "webhid",
        devices: Object.freeze(selected.map(toDeviceInfo)),
      })
      return false
    }
    return open(scanner)
  }

  async function reconnect() {
    if (hid == null || disposed) return false
    startListening()
    const knownDevices = (await hid.getDevices()).filter((value) =>
      scannerFilters.some((filter) => filter.vendorId === value.vendorId)
    )
    if (debug && knownDevices.length > 0) {
      console.groupCollapsed(`[BarcodeScanner/WebHID] reconnect found ${knownDevices.length} permitted device(s)`)
      for (const knownDevice of knownDevices) logDevice("permitted device", knownDevice)
      console.groupEnd()
    }
    const scanners = knownDevices.filter(isBarcodeScanner)
    return scanners.length === 0 ? false : open(scanners[0]!)
  }

  async function disconnect() {
    try {
      await closeCurrent()
    } finally {
      stopListening()
    }
  }

  return Object.freeze({
    transport: "webhid" as const,
    supported: hid != null,
    get connectedDevice() {
      return device == null ? null : toDeviceInfo(device)
    },
    connect,
    reconnect,
    disconnect,
    async dispose() {
      if (disposed) return
      disposed = true
      try {
        await closeCurrent()
      } finally {
        stopListening()
      }
    },
  })
}

function isHoneywell(device: HidDeviceLike) {
  return device.vendorId === 0x0c2e || device.vendorId === 0x23d0
}

function formatHex(value: number) {
  return `0x${value.toString(16).padStart(4, "0")}`
}

const honeywellSymbologies: Readonly<Record<number, string>> = Object.freeze({
  0x2c: "info-mail",
  0x3c: "code32",
  0x3f: "korea-post",
  0x41: "australian-post",
  0x42: "british-post",
  0x43: "canadian-post",
  0x44: "ean8",
  0x45: "upce",
  0x48: "chinese-sensible-code",
  0x4a: "japanese-post",
  0x4b: "kix",
  0x4c: "planet-code",
  0x4d: "imbc",
  0x4e: "postal-4i",
  0x50: "postnet",
  0x51: "china-post",
  0x52: "pdf417-micro",
  0x54: "code39-tcif",
  0x56: "codablock-a",
  0x59: "nec-2-of-5",
  0x61: "codabar",
  0x62: "code39",
  0x63: "upca",
  0x64: "ean13",
  0x65: "interleaved-2-of-5",
  0x66: "straight-2-of-5",
  0x67: "msi",
  0x68: "code11",
  0x69: "code93",
  0x6a: "code128",
  0x6c: "code49",
  0x6d: "matrix-2-of-5",
  0x71: "codablock-f",
  0x72: "pdf417",
  0x73: "qr-code",
  0x74: "telepen",
  0x77: "data-matrix",
  0x78: "maxicode",
  0x79: "gs1-databar-omni",
  0x7a: "aztec-code",
  0x7b: "gs1-databar-limited",
  0x7c: "gs1-128",
  0x7d: "gs1-databar-expanded",
})
